import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { Drawer } from 'components/ui/Drawer';
import { AskGuruChat } from 'schools/parashari/components/ask/AskGuruChat';
import { BhriguChatTrigger } from 'schools/parashari/components/ask/BhriguChatTrigger';
import { useFeatureVisibility } from 'schools/parashari/contexts/FeatureVisibilityContext';
import { useIsAdmin } from 'schools/parashari/hooks/useIsAdmin';
import { usePlatformTheme } from 'schools/parashari/hooks/usePlatformTheme';
import { useBhriguContextStack } from 'features/ai/hooks/useBhriguContextStack';
import { useAskGuruEnrollmentAccess } from 'features/ai/hooks/useAskGuruEnrollmentAccess';
import { shouldShowPlatformBhriguBubble } from 'shared/lib/bhriguBubbleVisibility';
import { getAskGuideForSchool } from 'features/ai/lib/askGuideIdentity';
import { ROUTES } from 'constants/routes';

/**
 * School-learn AskGuru bubble (KP, BNN, Jaimini, …).
 * Hidden on dashboard, settings, batches, catalog, and other non-learn pages -
 * anytime chat uses the dedicated AskGuru page.
 * Parashari Jyotisha learn uses PageFeedbackBubble inside ParashariShell instead.
 * Guide identity follows the active school route (or chip scope).
 */
export function BhriguFloatingChat({ pathname }) {
  const navigate = useNavigate();
  const { isDark } = usePlatformTheme();
  const { isFeatureVisible } = useFeatureVisibility();
  const { isAdmin } = useIsAdmin();
  const { stack, pageKey, pageTitle } = useBhriguContextStack();
  const { hasEnrollmentForPath } = useAskGuruEnrollmentAccess();
  const [open, setOpen] = useState(false);
  const [conversationActive, setConversationActive] = useState(false);
  const [activeSchool, setActiveSchool] = useState(
    () => stack?.schoolSlug || 'parashari',
  );
  const triggerRef = useRef(null);

  useEffect(() => {
    if (stack?.schoolSlug) setActiveSchool(stack.schoolSlug);
  }, [stack?.schoolSlug]);

  const guide = getAskGuideForSchool(activeSchool);

  const show = shouldShowPlatformBhriguBubble({
    pathname,
    isFeatureVisible: (key) => isFeatureVisible(key),
    isAdmin,
    hasEnrolledCourseAccess: hasEnrollmentForPath,
  });

  useEffect(() => {
    if (!show) setOpen(false);
  }, [show]);

  if (typeof document === 'undefined' || !show) return null;

  const closeDrawer = () => {
    setOpen(false);
    setConversationActive(false);
  };

  const panelBg = 'border-border bg-surface-secondary text-content';

  const onLoginClick = () => navigate(ROUTES.LOGIN);

  return createPortal(
    <>
      <Drawer
        open={open}
        onClose={closeDrawer}
        zIndexClass="z-[10050]"
        className="page-bhrigu-drawer"
        data-leave-guard-skip
        triggerRef={triggerRef}
        aria-label={guide.shortName}
        backdropAriaLabel={`Close ${guide.shortName}`}
        panelClassName={`page-bhrigu-drawer__panel z-[10051] w-full max-w-[min(100%,calc(100vw-2.5rem))] border-l sm:max-w-md ${
          conversationActive
            ? 'page-bhrigu-drawer__panel--chat'
            : 'page-bhrigu-drawer__panel--landing'
        } ${panelBg}`}
      >
        <header className="flex shrink-0 items-start justify-between gap-2 border-b border-border px-3 py-2.5">
          <div className="min-w-0 flex-1">
            <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-accent-strong">
              <img
                src={guide.faceUrl}
                alt=""
                className={`h-8 w-8 shrink-0 rounded-full bg-transparent object-cover ${
                  guide.faceObjectPosition === 'top' ? 'object-top' : 'object-center'
                }`}
                aria-hidden
              />
              {guide.shortName}
            </p>
            <p className="truncate text-sm font-semibold" title={pageTitle}>
              {pageTitle}
            </p>
            {guide.scopedSchoolOnly ? (
              <p className="mt-0.5 text-2xs leading-snug text-content-secondary">
                {guide.scopeHintEn}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={closeDrawer}
            className="shrink-0 rounded-md p-1 text-content-secondary transition-colors hover:bg-surface-tertiary hover:text-content"
            aria-label="Close"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </header>

        <div
          className={`page-bhrigu-drawer__body flex min-h-0 flex-1 flex-col overflow-hidden px-2 py-2 sm:px-3 sm:py-3 ${
            conversationActive ? '' : 'page-bhrigu-drawer__body--landing'
          }`}
        >
          <AskGuruChat
            isDark={isDark}
            onLoginClick={onLoginClick}
            compact
            embedded
            pageKey={pageKey}
            pageTitle={pageTitle}
            contextStack={stack}
            onConversationActiveChange={setConversationActive}
            onActiveSchoolChange={setActiveSchool}
          />
        </div>
      </Drawer>

      <div
        data-page-feedback-root
        data-leave-guard-skip
        className="page-feedback-root page-feedback-root--bhrigu page-feedback-root--mobile-tab fixed bottom-5 right-5 z-[45] flex flex-col items-end gap-2 sm:bottom-8 sm:right-8"
      >
        <BhriguChatTrigger
          ref={triggerRef}
          isDark={isDark}
          open={open}
          onClick={() => setOpen((v) => !v)}
          pageKey={pageKey ?? 'platform'}
          ariaLabel={guide.ariaAskEn}
          faceUrl={guide.faceUrl}
          faceObjectPosition={guide.faceObjectPosition}
          title={guide.shortName}
        />
      </div>
    </>,
    document.body
  );
}
